import React, { useEffect, useState, useMemo } from "react";
import { NavigationTabs } from "@/components/common/navigationTabs";
import { TableContainer } from "@/components/common/TableContainer";
import { DataView } from "@/components/common/DataView";
import { ActionButton } from "@/components/common/DataViewTypes";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchCategoriesRequest } from "@/store/slices/category.slice";
import { toast } from "sonner";
import { Plus, RefreshCw } from "lucide-react";
import { RootState } from "@/store/store";
import { Category } from "@/types/models/category";
import { getColumns } from "./columns";

const collectionTabs = [
  { label: "Active", status: "ACTIVE" },
  { label: "Draft", status: "DRAFT" },
  { label: "Archived", status: "ARCHIVED" },
  { label: "All", status: null },
];

const CollectionPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const storeType = params.storeType as string | undefined;
  const storeId = params.storeId as string | undefined;

  // Get columns with current store ID for owner comparison
  const columns = useMemo(() => getColumns(storeId), [storeId]);

  const { items, loading, pagination, error } = useAppSelector((state: RootState) => state.category);

  const [pageIndex, setPageIndex] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [activeTab, setActiveTab] = useState<string | null>("ACTIVE");

  const fetchCollections = () => {
    const requestParams: any = {
      page: pageIndex + 1,
      pageSize: pagination.pageSize,
      filters: {},
    };

    if (activeTab) {
      requestParams.filters.status = activeTab;
    }

    if (searchQuery.trim()) {
      requestParams.filters.name = searchQuery;
    }

    dispatch(fetchCategoriesRequest(requestParams));
  };

  useEffect(() => {
    // Only fetch if we don't have data OR if page/search/tab changed
    if ((items?.length || 0) === 0 || pageIndex > 0 || searchQuery.trim() || activeTab !== "ACTIVE") {
      fetchCollections();
    }
  }, [dispatch, pageIndex, pagination.pageSize, searchQuery, activeTab]);

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPageIndex(0);
  };

  const handleTabChange = (status: string | null) => {
    setActiveTab(status);
    setPageIndex(0);
  };

  const handlePageChange = (newPageIndex: number) => setPageIndex(newPageIndex);

  const getStatusTitle = () => {
    if (!activeTab) return "All Collections";
    switch (activeTab) {
      case "ACTIVE":
        return "Active Collections";
      case "DRAFT":
        return "Draft Collections";
      case "ARCHIVED":
        return "Archived Collections";
      default:
        return "Collections";
    }
  };

  const getStatusDescription = () => {
    if (!activeTab) return "Organize your products with collections for better management";
    switch (activeTab) {
      case "ACTIVE":
        return "Collections currently available and visible to customers";
      case "DRAFT":
        return "Collections in development or awaiting approval";
      case "ARCHIVED":
        return "Collections that are no longer in use";
      default:
        return "Organize your products with collections";
    }
  };

  const handleRefresh = () => {
    const requestParams: any = {
      page: pageIndex + 1,
      pageSize: pagination.pageSize,
      filters: {},
    };
    if (activeTab) requestParams.filters.status = activeTab;
    if (searchQuery.trim()) requestParams.filters.name = searchQuery;
    dispatch(fetchCategoriesRequest(requestParams));
  };

  const actionButtons: ActionButton[] = [
    {
      label: "Refresh",
      icon: <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />,
      onClick: handleRefresh,
      variant: "outline",
      disabled: loading,
    },
    {
      label: "Add Collection",
      icon: <Plus className="h-3 w-3" />,
      onClick: () => navigate(`/${storeType}/${storeId}/product/collection/create`),
      variant: "primary",
    },
  ];

  const currentPagination = {
    totalPages: pagination?.totalPages || 1,
    currentPage: (pagination?.page || 0) + 1,
    totalItems: pagination?.total || 0,
    pageSize: pagination?.pageSize || 10,
    total: pagination?.total || 0,
  };

  return (
    <div className="space-y-4">
      <TableContainer
        navigationTabs={
          <NavigationTabs
            tabs={collectionTabs.map((tab) => ({
              label: tab.label,
              onClick: () => handleTabChange(tab.status),
              isActive: activeTab === tab.status,
            }))}
            hasBackButton={false}
          />
        }
      >
        <div className="bg-secondary-background p-[18px] border rounded-[16px]">
          <DataView
            columns={columns}
            data={items || []}
            pagination={currentPagination}
            pageIndex={pageIndex}
            onPageChange={handlePageChange}
            loading={loading}
            error={error?.message || null}
            title={getStatusTitle()}
            description={getStatusDescription()}
            searchPlaceholder="Search collections..."
            searchQuery={searchQuery}
            onSearch={handleSearch}
            actionButtons={actionButtons}
            enableColumnResizing={true}
            enableRowSelection={false}
            emptyMessage="No collections found."
            loadingMessage="Loading..."
          />
        </div>
      </TableContainer>
    </div>
  );
};

export default CollectionPage;

